import { Dialog } from '@angular/cdk/dialog';
import { ApplicationRef, Injectable, NgZone, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { environment } from '@src/environments/environment';
import { Subject, Subscription } from 'rxjs';

import { AppTheme } from './app.theme';
import { AttachmentErrorComponent } from './common/components/attachments-list/modal/error/attachment-error';
import { EnpsRecordControl } from './common/controls/enps-record-control/enps-record-control';
import { DataStore } from './common/data-models/data-store/data-store';
import { PSTFieldNames } from './common/data-models/fields/pst-field-names';
import { SearchResults } from './common/data-models/search-results/search-results';
import { UsersResults } from './common/data-models/search-results/users-result';
import { ServiceRecord } from './common/data-models/service/service-record';
import { ClipboardTypes } from './common/enums/clipboard-types';
import { ObjectTypes } from './common/enums/object-types';
import { AttachmentService } from './common/services/attachment-service';
import { BasicContentService } from './common/services/basic-content-service';
import { GlobalTablesService } from './common/services/global-tables-service';
import { LanguageSettings } from './common/services/language-settings';
import { LayoutState } from './common/services/layout-state';
import { LogonService } from './common/services/logon-service';
import { MapService } from './common/services/map-service';
import { NavigationService } from './common/services/navigation-service';
import { NomWebServicesJson } from './common/services/nom-web-services-json';
import { RealtimeUpdatesSignalR } from './common/services/realtime-updates-signalr';
import { UserLocationService } from './common/services/user-location-service';
import { VisualStateService } from './common/services/visual-state-service';
import { IAppService } from './i-app.service';
import { ClipboardComponent } from './platform/components/clipboard/clipboard';

@Injectable({ providedIn: 'root' })
export class AppService implements IAppService {
    public readonly MAX_TITLE_LENGTH: number = 50;
    public PSTFieldNames = PSTFieldNames;

    public applicationName: string = '';
    public applicationEmbeddedName: string = '';

    // Clipboard
    public clipboard: ClipboardComponent;

    // Sidebar
    public sideBarExpanded = signal(true);

    public loginMessage: string = '';
    public loginShowAlert: boolean = false;

    // Filter

    private filterHidden: boolean = false;
    private filterHiddenSource = new Subject<boolean>();
    public filterHiddenObservable = this.filterHiddenSource.asObservable();

    public LoadingData: boolean = false;
    public LoadingDataSource = new Subject<boolean>();
    public LoadingDataObservable = this.LoadingDataSource.asObservable();

    // about menu

    // Topbar user menu
    public isUserMenuOpen: boolean = false;
    public previousUrl = '';

    private helloResultsSubscription: Subscription;

    // ENPS Desktop

    // Mobile Top Bar

    public redirectedPath = null;

    private _groupByUsers: Array<{ id: string; title: string }> = [];
    public get groupByUsers(): Array<{ id: string; title: string }> {
        if (this._groupByUsers.length === 0 && this.languageSettings) {
            this._groupByUsers = [
                { id: PSTFieldNames.UserInfoStatus, title: this.languageSettings.PSTWebUserGroupSortByStatusLabel },
                { id: PSTFieldNames.UserInfoProgram, title: this.languageSettings.PSTWebUserGroupSortByProgramLabel },
                { id: PSTFieldNames.UserInfoRole, title: this.languageSettings.PSTWebUserGroupSortByRoleLabel },
            ];
        }
        return this._groupByUsers;
    }

    private _groupByUsersKeys: Array<string> = [];
    public get groupByUsersKeys(): Array<string> {
        if (this._groupByUsersKeys.length === 0 && this.groupByUsers.length > 0) {
            this._groupByUsersKeys = this.groupByUsers.map(item => item.id);
        }
        return this._groupByUsersKeys;
    }

    // SSO providers

    public userResources: Array<StorytellingUserRecord> = [];

    public appSupportMap: {
        [id: string]: { supportsSearchMode: boolean; redirectTo: string; supportsDuplicate: boolean; supportsClone: boolean; supportsSaveTemplate: boolean; supportsCopyToClipboard: boolean };
    } = {
        playbook: {
            supportsSearchMode: true,
            redirectTo: 'playbook',
            supportsDuplicate: true,
            supportsClone: true,
            supportsSaveTemplate: true,
            supportsCopyToClipboard: true,
        },
        cast: {
            supportsSearchMode: false,
            redirectTo: 'playbook',
            supportsDuplicate: false,
            supportsClone: false,
            supportsSaveTemplate: false,
            supportsCopyToClipboard: false,
        },
    };

    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public logonService: LogonService,
        public globalTablesService: GlobalTablesService,
        public basicContentService: BasicContentService,
        public attachmentService: AttachmentService,
        public userLocationService: UserLocationService,
        private realtimeUpdatesSignalR: RealtimeUpdatesSignalR,
        public theme: AppTheme,
        public router: Router,
        public activatedRoute: ActivatedRoute,
        public viewStateService: LayoutState,
        public ngZone: NgZone,
        public applicationRef: ApplicationRef,
        public navigationService: NavigationService,
        public languageSettings: LanguageSettings,
        public visualStateService: VisualStateService,
        public dialogService: Dialog,
        public mapService: MapService,
        public store: Store
    ) {
        DataStore.appService = this;
        DataStore.store = this.store;
        SearchResults.appService = this;
        UsersResults.appService = this;
        EnpsRecordControl.appService = this;
        EnpsRecordControl.globalTablesService = this.globalTablesService;
        EnpsRecordControl.basicContentService = this.basicContentService;
        DataStore.addRealtimeUpdatesProvider(this.realtimeUpdatesSignalR);

        if (this.nomWebServicesJson.HelloResults) {
            this.helloResultsLoaded({ loaded: true, login: false });
        } else {
            if (this.helloResultsSubscription) {
                try {
                    this.helloResultsSubscription.unsubscribe();
                } catch (e) {
                    console.log(e);
                }
            }
            this.helloResultsSubscription = this.nomWebServicesJson.HelloResultsLoadedObservable.subscribe(this.helloResultsLoaded.bind(this));
        }

        // Start Ping thread
        setInterval(() => {
            if (!this.nomWebServicesJson.noSession) {
                this.nomWebServicesJson.ping();
            }
        }, 60000);
    }

    public forceReload() {
        if (this.visualStateService.hasIOSInterOp()) {
            window['webkit'].messageHandlers.interOp.postMessage({ name: 'forceReload', url: encodeURI(window.location.href) });
        }
    }

    public setLoginError() {
        if (this.visualStateService.hasIOSInterOp()) {
            window['webkit'].messageHandlers.interOp.postMessage({ name: 'loginError', result: 'true' });
        }
        if (window['ANDROID_WRAPPER']) {
            window['ANDROID_WRAPPER'].logout();
        }
    }

    public getObjectTypeName(objectType: number) {
        switch (objectType) {
            case ObjectTypes.STORYBOARD:
                return 'STORYBOARD';
            case ObjectTypes.RUNDOWN:
                return 'RUNDOWN';
            case ObjectTypes.SCRIPT:
                return 'STORY';
            default:
                return 'undefined';
        }
    }

    // Attachments
    public get attachmentsAllowed(): boolean {
        return this.visualStateService.ConfigSettings.ExternalDocuments === '1';
    }

    // Field groups order
    public get fieldGroupsOrder(): string {
        if (this.visualStateService.ConfigSettings.FieldGroupsOrder) {
            return this.visualStateService.ConfigSettings.FieldGroupsOrder;
        }
        return '';
    }

    // Cast restricted publication channel types
    public get restrictedPublicationChannelTypes(): Array<string> {
        if (this.visualStateService.ConfigSettings.PSTRestrictedPublicationChannelTypes) {
            const res = (<string>this.visualStateService.ConfigSettings.PSTRestrictedPublicationChannelTypes).split('|');
            return res;
        }
        return [];
    }

    public get allowedContentTypes(): Array<string> {
        return this.visualStateService.ConfigSettings.AllowedExternalDocs?.split(',')?.map(e => e.trim().toLowerCase()) || [];
    }

    public get allowedContentTypesLabel(): string {
        return this.visualStateService.ConfigSettings.AllowedExternalDocs?.split(',')?.length > 0 ? this.visualStateService.ConfigSettings.AllowedExternalDocs?.split(',') : 'NONE';
    }

    public isFileAllowedForUpload(filename: string) {
        const chunks = filename.split('.');
        const fileExtension = chunks[chunks.length - 1]?.toLowerCase();
        return this.allowedContentTypes.includes(fileExtension);
    }

    public async readUrl(files: Array<any>) {
        const res = [];
        for (const file in files) {
            const reader = new FileReader();
            const ofile = file;
            reader.onloadend = event => {
                files[ofile].finished = true;
                const base64File = event.target.result.toString().split('base64,')[1];
                res.push({
                    Type: ObjectTypes.EXTERNAL,
                    FileBytes: base64File,
                    Title: files[ofile].name,
                    Filename: files[ofile].name,
                });
            };
            reader.readAsDataURL(files[ofile].file);
        }
        await this.checkState(files);
        return res;
    }

    public async checkState(files) {
        let unfinished: boolean = false;
        while (!unfinished) {
            for (const file of files) {
                if (!file.finished) unfinished = true;
            }
            if (unfinished) await new Promise(resolve => setTimeout(resolve, 100));
        }
    }

    public async loadSessionData(): Promise<boolean> {
        try {
            const userId = localStorage.getItem('SessionUserId');
            const nomTokenId = localStorage.getItem('SessionNomTokenId');
            const helloResults = localStorage.getItem('GlobalsHelloResults');
            const hasSSOProviders: boolean = !!JSON.parse(localStorage.getItem('HasSSOProviders')) || this.nomWebServicesJson.ssoProviders?.length > 0;
            const providerName = localStorage.getItem('providerName') ? localStorage.getItem('providerName') : '';
            const idToken = localStorage.getItem('idToken') ? localStorage.getItem('idToken') : '';
            if (userId && nomTokenId && helloResults) {
                this.nomWebServicesJson.UserId = userId;
                this.nomWebServicesJson.NomTokenId = nomTokenId;
                this.nomWebServicesJson.HelloResults = JSON.parse(helloResults);
                this.nomWebServicesJson.HasSSOProviders = hasSSOProviders;

                this.visualStateService.getExpanderSettings();
                if (this.logonService.logonPassed) {
                    await this.helloResultsLoaded({ loaded: true, login: true });
                    return true;
                }
                const pingRes = await this.logonService.ping();
                if (pingRes && pingRes.status) {
                    await this.helloResultsLoaded({ loaded: true, login: false });
                    return true;
                }
                this.nomWebServicesJson.clearSessionData();
                return false;
            } else if (providerName && idToken) {
                return false;
            }
            this.nomWebServicesJson.clearSessionData();
            return false;
        } catch (e) {
            this.nomWebServicesJson.clearSessionData();
            return true;
        }
    }

    public async helloResultsLoaded(status: any) {
        if (status.loaded) {
            this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[ClipboardTypes.Topic] = 0;
            this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[ClipboardTypes.Assignment] = 0;
            this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[ClipboardTypes.TopicView] = 0;
            this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[ClipboardTypes.AssignmentView] = 0;
            this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[ClipboardTypes.SearchView] = 0;
            this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[ClipboardTypes.Story] = 0;
            this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[ClipboardTypes.StoryView] = 0;
            if (this.nomWebServicesJson.HelloResults.PersonalClipboardTemplates && this.nomWebServicesJson.HelloResults.PersonalClipboardTemplates.length) {
                const tmpEfTemplates: { [id: string]: StorytellingExportFormatRecord } = {};
                for (const rec of this.nomWebServicesJson.HelloResults.PersonalClipboardTemplates) {
                    const serviceRecord: ServiceRecord = ServiceRecord.instance(rec);
                    const tmpRec = this.nomWebServicesJson.convertor.fromServiceExportFormatRecord(serviceRecord);
                    tmpEfTemplates[tmpRec.PSTExportSourceObjectType] = tmpRec;
                    this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[tmpRec.PSTExportSourceObjectType]++;
                }
                this.nomWebServicesJson.HelloResults.PersonalExportFormatTemplates = tmpEfTemplates;
            }
            if (this.nomWebServicesJson.HelloResults.TeamClipboardTemplates && this.nomWebServicesJson.HelloResults.TeamClipboardTemplates.length) {
                const tmpEfTemplates: { [id: string]: StorytellingExportFormatRecord } = {};
                for (const rec of this.nomWebServicesJson.HelloResults.TeamClipboardTemplates) {
                    const serviceRecord: ServiceRecord = ServiceRecord.instance(rec);
                    const tmpRec = this.nomWebServicesJson.convertor.fromServiceExportFormatRecord(serviceRecord);
                    tmpEfTemplates[tmpRec.PSTExportSourceObjectType] = tmpRec;
                    this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[tmpRec.PSTExportSourceObjectType]++;
                }
                this.nomWebServicesJson.HelloResults.TeamExportFormatTemplates = tmpEfTemplates;
            }
            if (this.nomWebServicesJson.HelloResults.ClipboardTemplates && this.nomWebServicesJson.HelloResults.ClipboardTemplates.length) {
                const tmpEfTemplates: { [id: string]: StorytellingExportFormatRecord } = {};
                for (const rec of this.nomWebServicesJson.HelloResults.ClipboardTemplates) {
                    const serviceRecord: ServiceRecord = ServiceRecord.instance(rec);
                    const tmpRec = this.nomWebServicesJson.convertor.fromServiceExportFormatRecord(serviceRecord);
                    tmpEfTemplates[tmpRec.PSTExportSourceObjectType] = tmpRec;
                    this.nomWebServicesJson.ClipboardTemplateSourceCountPerType[tmpRec.PSTExportSourceObjectType]++;
                }
                this.nomWebServicesJson.HelloResults.EnterpriseExportFormatTemplates = tmpEfTemplates;
            }
        }
    }

    private solrPollInterval: number;
    public getSolrPollInvervalInSeconds() {
        if (this.solrPollInterval) {
            return this.solrPollInterval;
        }

        this.solrPollInterval = environment.solr_poll_interval_seconds ? 10000 : environment.solr_poll_interval_seconds * 1000;
        return this.solrPollInterval;
    }

    public delayTimelineInit: boolean = false;

    public async scrollCurrentListViewToTop() {
        if (this.navigationService.currentListView && this.navigationService.currentListView.container && this.navigationService.currentListView.container.nativeElement) {
            this.navigationService.currentListView.container.nativeElement.scrollTop = 0;
        }
    }

    public isPreviousRouteList(): boolean {
        if (!this.previousUrl) this.previousUrl = localStorage.getItem('prevUrl');
        if (
            this.previousUrl.indexOf('filter') > -1 ||
            this.previousUrl.indexOf('groupby') > -1 ||
            this.previousUrl.indexOf('sortby') > -1 ||
            this.previousUrl.indexOf('add_topic') > -1 ||
            this.previousUrl.indexOf('view_topic') > -1 ||
            this.previousUrl.indexOf('add_assignment') > -1 ||
            this.previousUrl.indexOf('edit_assignment') > -1
        ) {
            return false;
        }
        return true;
    }

    public getUserIDV2(rec: StorytellingRecord): string {
        if (!rec) return '';

        switch (rec.Type) {
            case ObjectTypes.TOPIC:
                return (rec as StorytellingTopicRecord)?.PSTTopicOwner?.[0] || '';
            case ObjectTypes.ASSIGNMENT:
                return (rec as StorytellingAssignmentRecord)?.PSTAssignmentOwner?.[0] || '';
            case ObjectTypes.SCRIPT:
                return (rec as StorytellingStoryRecord)?.PSTStoryOwner?.[0] || '';
            case ObjectTypes.USER:
                return rec.Owner;
            default:
                return '';
        }
    }
    public getUserNameV2(rec: StorytellingRecord): string {
        if (!rec) return '';

        const userID = this.getUserIDV2(rec);
        if (userID) {
            return this.globalTablesService.Staff[userID] ? `${this.globalTablesService.Staff[userID].GivenName} ${this.globalTablesService.Staff[userID].Surname}` : 'Unknown';
        }
    }

    public async shouldProceedAttachment(name: string, size: number): Promise<boolean> {
        if (!this.isFileAllowedForUpload(name) || size > this.attachmentService.maxExternalDocumentSize) {
            await this.openAttachmentErrAlert(name);
            return false;
        }
        return true;
    }

    public async openAttachmentErrAlert(name?: string) {
        await this.dialogService
            .open(AttachmentErrorComponent, {
                data: {
                    title: `${this.languageSettings.PSTWebErrorUploadingFileLabel}${name?.length > 0 ? `: ${name}` : ''}`,
                    messages: [
                        `${this.languageSettings.PSTWebAllowedFileTypesText} ${this.allowedContentTypesLabel}`,
                        `${this.languageSettings.PSTWebAllowedFileSizeText} ${this.attachmentService.maxExternalDocumentSize / 1000} kb`,
                    ],
                },
            })
            .closed.toPromise();
    }
}
